import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft, Eye, Save } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import SellOutAllocationPreview from '../../components/sellout/SellOutAllocationPreview';
import SellOutCreditNoteForm from '../../components/sellout/SellOutCreditNoteForm';
import SellOutDocumentHeaderForm from '../../components/sellout/SellOutDocumentHeaderForm';
import SellOutReceiptLineTable, {
  makeSellOutReceiptLine,
  resolveReceiptQuantity,
  resolveReceiptSourceField,
} from '../../components/sellout/SellOutReceiptLineTable';
import SellOutReceiveNoteLink from '../../components/sellout/SellOutReceiveNoteLink';
import {
  commitmentLineLabel,
  documentTypeLabel,
  documentTypeOptions,
  formatDate,
  formatMoney,
  numberValue,
  productLabel,
  supplierLabel,
  todayIso,
} from '../../components/sellout/selloutUtils';
import API, { SellOutAPI } from '../../services/api';
import { addTab } from '../../tabs/tabsSlice';

const steps = ['Documento', 'Detalle recibido', 'Asignacion', 'Nota entrada', 'Revision'];

const emptyForm = () => ({
  supplier_id: null,
  document_type: 'promo_invoice',
  number: '',
  series: '',
  date: todayIso(),
  total: '',
  file: null,
  commitment_id: null,
  linked_invoice_id: null,
  applied_amount: '',
  comment: '',
});

const makeClientId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);
const idsEqual = (left, right) => String(left || '') === String(right || '');

function findSourceLine(receiptLines, row) {
  return (
    (row.source_client_id
      ? receiptLines.find((line) => line.client_id === row.source_client_id)
      : null)
    || receiptLines.find((line) => idsEqual(line.real_product_id, row.real_product_id))
    || null
  );
}

export default function SellOutDocumentCreate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const initialDefaults = location.state?.selloutDocumentDefaults || {};

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(() => ({
    ...emptyForm(),
    ...initialDefaults,
    date: initialDefaults.date || todayIso(),
  }));
  const [suppliers, setSuppliers] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [commitmentLines, setCommitmentLines] = useState([]);
  const [promoInvoices, setPromoInvoices] = useState([]);
  const [receiptLines, setReceiptLines] = useState([makeSellOutReceiptLine()]);
  const [allocations, setAllocations] = useState([]);
  const [unassignedLines, setUnassignedLines] = useState([]);
  const [allocationWarnings, setAllocationWarnings] = useState([]);
  const [selectedReceiveNote, setSelectedReceiveNote] = useState(null);
  const [confirmations, setConfirmations] = useState({
    document: false,
    allocations: false,
    receiveNote: false,
  });
  const [errors, setErrors] = useState({ general: [], receiptLines: {} });
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loadingCommitments, setLoadingCommitments] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });

  const showSnack = useCallback((severity, message) => {
    setSnack({ open: true, severity, message });
  }, []);

  useEffect(() => {
    dispatch(addTab({ label: 'Ingreso documental Sell Out', path: '/sellout/documents/create' }));
  }, [dispatch]);

  useEffect(() => {
    let mounted = true;
    setLoadingSuppliers(true);
    API.get('suppliers/minimal/', { params: { active: true } })
      .then(({ data }) => {
        if (mounted) setSuppliers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (mounted) showSnack('error', 'No se pudo cargar proveedores.');
      })
      .finally(() => {
        if (mounted) setLoadingSuppliers(false);
      });
    return () => {
      mounted = false;
    };
  }, [showSnack]);

  const selectedSupplier = useMemo(
    () => suppliers.find((supplier) => idsEqual(supplier.id, form.supplier_id)) || null,
    [form.supplier_id, suppliers]
  );

  const selectedCommitment = useMemo(
    () => commitments.find((commitment) => idsEqual(commitment.id, form.commitment_id)) || null,
    [commitments, form.commitment_id]
  );

  const selectedInvoice = useMemo(
    () => promoInvoices.find((invoice) => idsEqual(invoice.id, form.linked_invoice_id)) || null,
    [form.linked_invoice_id, promoInvoices]
  );

  useEffect(() => {
    let mounted = true;
    if (!form.supplier_id) {
      setCommitments([]);
      return () => {
        mounted = false;
      };
    }
    setLoadingCommitments(true);
    SellOutAPI.commitments.list({ supplier_id: form.supplier_id })
      .then(({ data }) => {
        if (mounted) setCommitments(asArray(data));
      })
      .catch(() => {
        if (mounted) {
          setCommitments([]);
          showSnack('error', 'No se pudieron cargar promesas Sell Out.');
        }
      })
      .finally(() => {
        if (mounted) setLoadingCommitments(false);
      });
    return () => {
      mounted = false;
    };
  }, [form.supplier_id, showSnack]);

  useEffect(() => {
    let mounted = true;
    if (!form.supplier_id) {
      setCommitmentLines([]);
      return () => {
        mounted = false;
      };
    }
    setLoadingLines(true);
    const params = { supplier_id: form.supplier_id };
    if (form.commitment_id) params.commitment_id = form.commitment_id;
    SellOutAPI.commitmentLines.missing(params)
      .then(({ data }) => {
        if (mounted) setCommitmentLines(asArray(data));
      })
      .catch(() => {
        if (mounted) {
          setCommitmentLines([]);
          showSnack('error', 'No se pudieron cargar lineas abiertas de promesa.');
        }
      })
      .finally(() => {
        if (mounted) setLoadingLines(false);
      });
    return () => {
      mounted = false;
    };
  }, [form.commitment_id, form.supplier_id, showSnack]);

  useEffect(() => {
    let mounted = true;
    if (!form.supplier_id || form.document_type !== 'credit_note') {
      setPromoInvoices([]);
      return () => {
        mounted = false;
      };
    }
    setLoadingInvoices(true);
    SellOutAPI.documents.list({
      supplier_id: form.supplier_id,
      document_type: 'promo_invoice',
    })
      .then(({ data }) => {
        if (mounted) setPromoInvoices(asArray(data));
      })
      .catch(() => {
        if (mounted) {
          setPromoInvoices([]);
          showSnack('error', 'No se pudieron cargar facturas promocionales.');
        }
      })
      .finally(() => {
        if (mounted) setLoadingInvoices(false);
      });
    return () => {
      mounted = false;
    };
  }, [form.document_type, form.supplier_id, showSnack]);

  const updateForm = (patch) => {
    const supplierChanged = Object.prototype.hasOwnProperty.call(patch, 'supplier_id') && !idsEqual(patch.supplier_id, form.supplier_id);
    const typeChanged = Object.prototype.hasOwnProperty.call(patch, 'document_type') && patch.document_type !== form.document_type;

    if (supplierChanged || typeChanged) {
      setReceiptLines([makeSellOutReceiptLine()]);
      setAllocations([]);
      setUnassignedLines([]);
      setAllocationWarnings([]);
      setSelectedReceiveNote(null);
      setConfirmations({ document: false, allocations: false, receiveNote: false });
      setErrors({ general: [], receiptLines: {} });
      setActiveStep(0);
    }

    setForm((prev) => ({
      ...prev,
      ...patch,
      ...(supplierChanged ? { commitment_id: null, linked_invoice_id: null } : {}),
      ...(typeChanged ? { commitment_id: null, linked_invoice_id: null } : {}),
    }));
  };

  const validatePromoStep = (step, persist = true) => {
    const general = [];
    const lineErrors = {};

    if (!form.supplier_id) general.push('Seleccione proveedor.');
    if (!String(form.number || '').trim()) general.push('Ingrese numero de documento.');
    if (!form.date) general.push('Seleccione fecha.');
    if (form.total !== '' && numberValue(form.total) < 0) general.push('Total invalido.');

    if (step >= 1) {
      if (!receiptLines.length) general.push('Agregue al menos una linea recibida.');
      receiptLines.forEach((line, index) => {
        const messages = [];
        if (!line.real_product_id) messages.push(`Linea ${index + 1}: seleccione producto real.`);
        if (resolveReceiptQuantity(line) <= 0) messages.push(`Linea ${index + 1}: cantidad debe ser mayor a 0.`);
        if (messages.length) lineErrors[line.client_id] = messages;
      });
    }

    if (step >= 2) {
      if (!allocations.length) general.push('Ejecute el preview de asignacion.');
      if (unassignedLines.length) general.push('Asigne manualmente las lineas sin sugerencia antes de guardar.');
      allocations.forEach((row, index) => {
        if (!row.commitment_line_id) general.push(`Asignacion ${index + 1}: seleccione linea de promesa.`);
        if (numberValue(row.received_qty) <= 0) general.push(`Asignacion ${index + 1}: cantidad debe ser mayor a 0.`);
      });
    }

    if (step >= 4) {
      if (!confirmations.document || !confirmations.allocations || !confirmations.receiveNote) {
        general.push('Complete las confirmaciones finales.');
      }
    }

    if (persist) setErrors({ general, receiptLines: lineErrors });
    return general.length === 0 && Object.keys(lineErrors).length === 0;
  };

  const validateCreditNote = () => {
    const general = [];
    if (!form.supplier_id) general.push('Seleccione proveedor.');
    if (!form.linked_invoice_id) general.push('Seleccione factura promocional.');
    if (!String(form.number || '').trim()) general.push('Ingrese numero de nota de credito.');
    if (!form.date) general.push('Seleccione fecha.');
    if (numberValue(form.total) <= 0 && numberValue(form.applied_amount) <= 0) {
      general.push('Ingrese total o monto aplicado.');
    }
    setErrors({ general, receiptLines: {} });
    return general.length === 0;
  };

  const normalizePreviewRow = (row, forcedStatus) => {
    const realProductId = row.real_product_id || row.product_id || null;
    const commitmentLineId = row.commitment_line_id || row.sellout_commitment_line_id || null;
    const source = receiptLines.find((line) => idsEqual(line.real_product_id, realProductId)) || null;
    const received = numberValue(row.received_qty ?? row.qty);
    return {
      client_id: makeClientId(),
      source_client_id: source?.client_id || null,
      real_product_id: realProductId,
      real_product: source?.real_product || null,
      real_format_id: source?.real_format_id || null,
      commitment_line_id: commitmentLineId,
      received_qty: received,
      accepted_qty: numberValue(row.accepted_qty) || received,
      rejected_qty: numberValue(row.rejected_qty),
      confidence: row.confidence || (forcedStatus ? 'manual' : ''),
      reason: row.reason || '',
      allocation_status: forcedStatus || row.allocation_status || (commitmentLineId ? 'suggested' : 'requires_review'),
      source_quantity_field: source ? resolveReceiptSourceField(source) : 'received_qty',
      unit_cost: source?.unit_cost || '',
      comment: source?.comment || '',
    };
  };

  const runPreview = async () => {
    if (!validatePromoStep(1)) return false;
    setPreviewing(true);
    try {
      const payload = {
        supplier_id: form.supplier_id,
        commitment_id: form.commitment_id || null,
        document_type: 'promo_invoice',
        lines: receiptLines.map((line) => ({
          client_id: line.client_id,
          real_product_id: line.real_product_id,
          product_id: line.real_product_id,
          received_qty: resolveReceiptQuantity(line),
          accepted_qty: resolveReceiptQuantity(line),
          rejected_qty: 0,
          quantity: numberValue(line.quantity),
          quantityRe: numberValue(line.quantityRe),
        })),
      };
      const { data } = await SellOutAPI.documents.previewAllocation(payload);
      setAllocations(asArray(data?.suggested_allocations).map((row) => normalizePreviewRow(row)));
      setUnassignedLines(asArray(data?.unassigned_lines).map((row) => normalizePreviewRow(row, 'requires_review')));
      setAllocationWarnings(Array.isArray(data?.warnings) ? data.warnings : []);
      return true;
    } catch (error) {
      const detail = error?.response?.data;
      const message = typeof detail === 'string'
        ? detail
        : detail?.detail || 'No se pudo calcular el preview de asignacion.';
      showSnack('error', message);
      return false;
    } finally {
      setPreviewing(false);
    }
  };

  const uploadFile = async (documentId) => {
    if (!form.file || !documentId) return;
    const fd = new FormData();
    fd.append('file', form.file);
    await API.patch(`sellout/documents/${documentId}/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const buildLinePayload = (row) => {
    const source = findSourceLine(receiptLines, row);
    const received = numberValue(row.received_qty);
    const rejected = numberValue(row.rejected_qty);
    const acceptedRaw = row.accepted_qty === '' || row.accepted_qty === null || row.accepted_qty === undefined
      ? received
      : numberValue(row.accepted_qty);
    const accepted = Math.max(0, Math.min(acceptedRaw, Math.max(received - rejected, 0)));

    return {
      sellout_commitment_line_id: row.commitment_line_id,
      real_product_id: row.real_product_id || null,
      real_format_id: row.real_format_id || source?.real_format_id || null,
      received_qty: received,
      accepted_qty: accepted,
      rejected_qty: rejected,
      allocation_status: row.allocation_status || 'confirmed',
      source_quantity_field: row.source_quantity_field || (source ? resolveReceiptSourceField(source) : 'received_qty'),
      unit_cost: row.unit_cost || source?.unit_cost || null,
      comment: row.comment || source?.comment || null,
    };
  };

  const savePromoDocument = async () => {
    if (!validatePromoStep(4)) return;
    setSaving(true);
    try {
      const payload = {
        supplier_id: form.supplier_id,
        document_type: 'promo_invoice',
        number: String(form.number || '').trim(),
        series: String(form.series || '').trim() || null,
        date: form.date,
        total: form.total === '' ? null : numberValue(form.total),
        commitment_id: form.commitment_id || null,
        receive_note_id: selectedReceiveNote?.id || null,
        comment: form.comment || null,
        lines_payload: allocations.map(buildLinePayload),
      };
      const { data } = await SellOutAPI.documents.create(payload);
      let message = 'Documento Sell Out guardado.';
      try {
        await uploadFile(data?.id);
      } catch {
        message = 'Documento Sell Out guardado, pero no se pudo subir el archivo.';
      }
      navigate('/sellout/documents', {
        replace: true,
        state: { message },
      });
    } catch (error) {
      const detail = error?.response?.data;
      const message = typeof detail === 'string'
        ? detail
        : detail?.detail || detail?.lines_payload || 'No se pudo guardar el documento Sell Out.';
      showSnack('error', Array.isArray(message) ? message.join(' ') : String(message));
    } finally {
      setSaving(false);
    }
  };

  const saveCreditNote = async () => {
    if (!validateCreditNote()) return;
    setSaving(true);
    try {
      const total = numberValue(form.total);
      const applied = numberValue(form.applied_amount) || total;
      const payload = {
        supplier_id: form.supplier_id,
        document_type: 'credit_note',
        linked_invoice_id: form.linked_invoice_id,
        number: String(form.number || '').trim(),
        series: String(form.series || '').trim() || null,
        date: form.date,
        total: total || applied,
        applied_amount: applied,
        comment: form.comment || null,
      };
      const { data } = await SellOutAPI.documents.create(payload);
      let message = 'Nota de credito Sell Out guardada.';
      try {
        await uploadFile(data?.id);
      } catch {
        message = 'Nota de credito Sell Out guardada, pero no se pudo subir el archivo.';
      }
      navigate('/sellout/documents', {
        replace: true,
        state: { message },
      });
    } catch (error) {
      const detail = error?.response?.data;
      const message = typeof detail === 'string'
        ? detail
        : detail?.detail || detail?.linked_invoice_id || 'No se pudo guardar la nota de credito.';
      showSnack('error', Array.isArray(message) ? message.join(' ') : String(message));
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (activeStep === 1) {
      const ok = await runPreview();
      if (ok) setActiveStep(2);
      return;
    }
    if (!validatePromoStep(activeStep)) return;
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const allocationTotalsByLine = useMemo(() => {
    const map = new Map();
    allocations.forEach((row) => {
      const key = String(row.commitment_line_id || '');
      if (!key) return;
      map.set(key, (map.get(key) || 0) + numberValue(row.received_qty));
    });
    return map;
  }, [allocations]);

  const totalReceived = useMemo(
    () => receiptLines.reduce((sum, line) => sum + resolveReceiptQuantity(line), 0),
    [receiptLines]
  );

  const totalAllocated = useMemo(
    () => allocations.reduce((sum, row) => sum + numberValue(row.received_qty), 0),
    [allocations]
  );

  const renderFinalReview = () => (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <ReviewItem label="Proveedor" value={supplierLabel(selectedSupplier)} />
        <ReviewItem label="Documento" value={`${documentTypeLabel(form.document_type)} ${form.series ? `${form.series}-` : ''}${form.number}`} />
        <ReviewItem label="Fecha" value={formatDate(form.date)} />
        <ReviewItem label="Total" value={form.total === '' ? '-' : formatMoney(form.total)} />
        <ReviewItem label="Nota entrada" value={selectedReceiveNote ? `NR ${selectedReceiveNote.number}` : 'Sin vinculo'} />
      </Stack>

      <Divider />

      <Box sx={{ overflowX: 'auto' }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Lineas reales</Typography>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell>Producto real</TableCell>
              <TableCell width={130} align="right">Cantidad</TableCell>
              <TableCell>Comentario</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {receiptLines.map((line) => (
              <TableRow key={line.client_id}>
                <TableCell>{productLabel(line.real_product)}</TableCell>
                <TableCell align="right">{resolveReceiptQuantity(line)}</TableCell>
                <TableCell>{line.comment || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Asignaciones a promesas</Typography>
        <Table size="small" sx={{ minWidth: 820 }}>
          <TableHead>
            <TableRow>
              <TableCell>Linea promesa</TableCell>
              <TableCell width={130} align="right">Recibido</TableCell>
              <TableCell width={130} align="right">Faltante final</TableCell>
              <TableCell width={160}>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(allocationTotalsByLine.entries()).map(([lineId, qty]) => {
              const line = commitmentLines.find((item) => idsEqual(item.id, lineId));
              const before = numberValue(line?.total_missing);
              return (
                <TableRow key={lineId}>
                  <TableCell>{commitmentLineLabel(line) || `Linea #${lineId}`}</TableCell>
                  <TableCell align="right">{qty}</TableCell>
                  <TableCell align="right">{Math.max(before - qty, 0)}</TableCell>
                  <TableCell>Asignada</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <ReviewItem label="Total recibido" value={totalReceived} />
        <ReviewItem label="Total asignado" value={totalAllocated} />
        <ReviewItem label="Lineas con revision" value={allocations.filter((row) => row.allocation_status === 'requires_review').length} />
      </Stack>

      <Divider />

      <Stack spacing={0.5}>
        <FormControlLabel
          control={<Checkbox checked={confirmations.document} onChange={(event) => setConfirmations((prev) => ({ ...prev, document: event.target.checked }))} />}
          label="Documento revisado"
        />
        <FormControlLabel
          control={<Checkbox checked={confirmations.allocations} onChange={(event) => setConfirmations((prev) => ({ ...prev, allocations: event.target.checked }))} />}
          label="Asignaciones revisadas"
        />
        <FormControlLabel
          control={<Checkbox checked={confirmations.receiveNote} onChange={(event) => setConfirmations((prev) => ({ ...prev, receiveNote: event.target.checked }))} />}
          label="Nota de entrada validada"
        />
      </Stack>
    </Stack>
  );

  const renderPromoStep = () => {
    if (activeStep === 0) {
      return (
        <SellOutDocumentHeaderForm
          form={form}
          onChange={updateForm}
          suppliers={suppliers}
          selectedSupplier={selectedSupplier}
          commitments={commitments}
          selectedCommitment={selectedCommitment}
          loadingSuppliers={loadingSuppliers}
          loadingCommitments={loadingCommitments}
          disabled={saving}
        />
      );
    }
    if (activeStep === 1) {
      return (
        <SellOutReceiptLineTable
          lines={receiptLines}
          onChange={setReceiptLines}
          errors={errors.receiptLines}
          disabled={saving}
        />
      );
    }
    if (activeStep === 2) {
      return (
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="flex-end">
            <Button variant="outlined" startIcon={<Eye size={16} />} onClick={runPreview} disabled={previewing || loadingLines || saving}>
              Recalcular preview
            </Button>
          </Stack>
          <SellOutAllocationPreview
            allocations={allocations}
            onAllocationsChange={setAllocations}
            unassignedLines={unassignedLines}
            onUnassignedChange={setUnassignedLines}
            commitmentLines={commitmentLines}
            receiptLines={receiptLines}
            warnings={allocationWarnings}
            disabled={saving}
          />
        </Stack>
      );
    }
    if (activeStep === 3) {
      return (
        <SellOutReceiveNoteLink
          selectedNote={selectedReceiveNote}
          disabled={saving}
          onChange={(note) => {
            setSelectedReceiveNote(note);
            setForm((prev) => ({ ...prev, receive_note_id: note?.id || null }));
          }}
        />
      );
    }
    return renderFinalReview();
  };

  const isCreditNote = form.document_type === 'credit_note';

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', width: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Ingreso documental Sell Out</Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedSupplier ? supplierLabel(selectedSupplier) : documentTypeLabel(form.document_type)}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<ArrowLeft size={17} />} onClick={() => navigate('/sellout/documents')}>
          Volver
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 1 }}>
        {errors.general.length > 0 ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general.join(' ')}
          </Alert>
        ) : null}

        {isCreditNote ? (
          <Stack spacing={2}>
            <TextField
              select
              size="small"
              label="Tipo documento"
              value={form.document_type}
              onChange={(event) => updateForm({ document_type: event.target.value })}
              sx={{ maxWidth: 260 }}
            >
              {documentTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <SellOutCreditNoteForm
              form={form}
              onChange={updateForm}
              suppliers={suppliers}
              selectedSupplier={selectedSupplier}
              invoices={promoInvoices}
              selectedInvoice={selectedInvoice}
              loadingSuppliers={loadingSuppliers}
              loadingInvoices={loadingInvoices}
              disabled={saving}
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="outlined" onClick={() => navigate('/sellout/documents')} disabled={saving}>
                Cancelar
              </Button>
              <Button variant="contained" startIcon={<Save size={17} />} onClick={saveCreditNote} disabled={saving}>
                Guardar nota de credito
              </Button>
            </Stack>
          </Stack>
        ) : (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 2 }} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {renderPromoStep()}

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => setActiveStep((prev) => Math.max(prev - 1, 0))}
                disabled={activeStep === 0 || saving || previewing}
              >
                Atras
              </Button>
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={saving || previewing}
                >
                  {activeStep === 1 ? 'Preview de asignacion' : 'Continuar'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Save size={17} />}
                  onClick={savePromoDocument}
                  disabled={saving}
                >
                  Guardar documento
                </Button>
              )}
            </Stack>
          </>
        )}
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((prev) => ({ ...prev, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function ReviewItem({ label, value }) {
  return (
    <Box sx={{ minWidth: 170 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={700}>{value || '-'}</Typography>
    </Box>
  );
}
